import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import React from 'react';

/**
 * Custom hook for export orchestration.
 * Handles the report endpoints (PDF/PPTX/Word) plus raw data exports with async polling for large Excel files.
 */
export function useExportRunner({ API, exportFilters, filterOptions, exportGranularity, getFilterSummary }) {
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  const buildFilterContext = useCallback(() => {
    const parts = [];
    if (exportFilters.locations.length === 1) {
      parts.push(exportFilters.locations[0]);
    } else if (exportFilters.locations.length > 1 && exportFilters.locations.length < filterOptions.locations.length) {
      parts.push(`${exportFilters.locations.length} Locations`);
    }
    if (exportFilters.departments.length === 1) {
      parts.push(exportFilters.departments[0]);
    } else if (exportFilters.departments.length > 1 && exportFilters.departments.length < filterOptions.departments.length) {
      parts.push(`${exportFilters.departments.length} Departments`);
    }
    if (exportFilters.startDate && exportFilters.endDate) {
      parts.push(`${exportFilters.startDate} to ${exportFilters.endDate}`);
    } else if (exportFilters.startDate) {
      parts.push(`From ${exportFilters.startDate}`);
    }
    return parts;
  }, [exportFilters, filterOptions]);

  const buildComprehensiveFilters = useCallback(() => {
    const cf = {
      start_date: exportFilters.startDate || null,
      end_date: exportFilters.endDate || null,
      locations: exportFilters.locations.length > 0 && exportFilters.locations.length < filterOptions.locations.length
        ? exportFilters.locations : null,
      departments: exportFilters.departments.length > 0 && exportFilters.departments.length < filterOptions.departments.length
        ? exportFilters.departments : null,
      supervisors: exportFilters.supervisors.length > 0 && exportFilters.supervisors.length < filterOptions.supervisors.length
        ? exportFilters.supervisors : null,
      pay_types: exportFilters.payTypes.length > 0 && exportFilters.payTypes.length < filterOptions.payTypes.length
        ? exportFilters.payTypes : null,
      location: exportFilters.locations.length === 1 ? exportFilters.locations[0] : null,
      department: exportFilters.departments.length === 1 ? exportFilters.departments[0] : null
    };
    Object.keys(cf).forEach(key => { if (cf[key] === null) delete cf[key]; });
    return cf;
  }, [exportFilters, filterOptions]);

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildReportPayload = (exportType, comprehensiveFilters, filterContext, isFiltered) => {
    const baseTitle = {
      executive_summary: 'OvertimeIQ Executive Summary',
      powerpoint: 'OvertimeIQ Analysis',
      full_pdf: 'OvertimeIQ Full Report',
      word_document: 'OvertimeIQ Analysis Report'
    }[exportType];
    const subtitle = isFiltered ? 'Filtered Report' : (exportType === 'executive_summary' ? 'Complete Overview' : 'Complete Analysis');

    const common = {
      title: `${baseTitle}${filterContext}`,
      subtitle,
      filters: comprehensiveFilters,
      filter_description: getFilterSummary()
    };

    if (exportType === 'executive_summary') {
      return { ...common, include_recommendations: true, include_insights: true };
    }
    if (exportType === 'powerpoint') {
      return { ...common, include_charts: true, include_recommendations: true, include_insights: true };
    }
    return { ...common, include_all_data: true };
  };

  const reportEndpointFor = (exportType) => ({
    executive_summary: '/export/executive-summary',
    powerpoint: '/export/powerpoint',
    full_pdf: '/export/pdf',
    word_document: '/export/word'
  }[exportType]);

  const reportExtensionFor = (exportType) => ({
    executive_summary: '.pdf',
    powerpoint: '.pptx',
    full_pdf: '.pdf',
    word_document: '.docx'
  }[exportType]);

  const reportFilenamePrefixFor = (exportType) => ({
    executive_summary: 'executive_summary',
    powerpoint: 'overtime_analysis',
    full_pdf: 'overtime_report',
    word_document: 'overtime_analysis'
  }[exportType]);

  const startAsyncExcelPolling = async (payload, toastId) => {
    toast.loading(`Large file — generating Excel in background. You'll get a download link shortly.`, { id: toastId, duration: 60000 });

    const asyncResp = await axios.post(`${API}/export/data/async`, payload);
    const jobId = asyncResp.data.job_id;

    const pollInterval = setInterval(async () => {
      try {
        const statusResp = await axios.get(`${API}/export/status/${jobId}`);
        const status = statusResp.data.status;

        if (status === 'complete') {
          clearInterval(pollInterval);
          const downloadUrl = `${API}/export/download/${jobId}`;
          toast.success(
            React.createElement('div', null,
              React.createElement('p', { className: 'font-medium' }, 'Excel file ready!'),
              React.createElement('a', {
                href: downloadUrl,
                className: 'text-[#2E5C8A] underline text-sm',
                download: statusResp.data.filename
              }, `Click here to download (${(statusResp.data.size / 1024 / 1024).toFixed(1)} MB)`)
            ),
            { id: toastId, duration: 30000 }
          );
          setExportProgress(prev => ({ ...prev, status: 'complete' }));
          setTimeout(() => setExportProgress(null), 2000);
          setExporting(false);
        } else if (status === 'error') {
          clearInterval(pollInterval);
          toast.error(`Excel generation failed: ${statusResp.data.error}`, { id: toastId });
          setExportProgress(prev => ({ ...prev, status: 'error' }));
          setTimeout(() => setExportProgress(null), 3000);
          setExporting(false);
        } else {
          toast.loading(`Generating Excel (${Number(statusResp.data.row_count).toLocaleString()} rows)...`, { id: toastId });
        }
      } catch {
        clearInterval(pollInterval);
        toast.error('Lost connection while generating file. Please try again.', { id: toastId });
        setExporting(false);
      }
    }, 3000);
  };

  const handleExport = useCallback(async (exportType, format) => {
    setExporting(true);
    setExportProgress({ type: exportType, format, status: 'preparing' });
    const toastId = toast.loading(`Preparing ${exportType.replace('_', ' ')} export...`);

    try {
      const filterParts = buildFilterContext();
      const filterContext = filterParts.length > 0 ? ` - ${filterParts.join(', ')}` : '';
      const isFiltered = filterParts.length > 0;
      const comprehensiveFilters = buildComprehensiveFilters();

      setExportProgress(prev => ({ ...prev, status: 'generating' }));
      toast.loading('Generating file...', { id: toastId });

      const dateStr = new Date().toISOString().split('T')[0];
      const safeFilterContext = filterContext.replace(/[^a-zA-Z0-9\-_ ]/g, '').trim();
      const filterSuffix = safeFilterContext ? '_' + safeFilterContext.replace(/ /g, '_') : '';

      let response;
      let filename;

      const reportEndpoint = reportEndpointFor(exportType);
      if (reportEndpoint) {
        const payload = buildReportPayload(exportType, comprehensiveFilters, filterContext, isFiltered);
        response = await axios.post(`${API}${reportEndpoint}`, payload, {
          responseType: 'blob',
          timeout: 120000
        });
        filename = `${reportFilenamePrefixFor(exportType)}${filterSuffix}_${dateStr}${reportExtensionFor(exportType)}`;
      } else {
        // Raw data exports (CSV, Excel, JSON)
        const payload = {
          export_type: exportType,
          format: format,
          granularity: exportGranularity,
          filters: {
            start_date: exportFilters.startDate || null,
            end_date: exportFilters.endDate || null,
            locations: exportFilters.locations.length > 0 ? exportFilters.locations : null,
            departments: exportFilters.departments.length > 0 ? exportFilters.departments : null,
            supervisors: exportFilters.supervisors.length > 0 ? exportFilters.supervisors : null,
            job_titles: exportFilters.jobTitles.length > 0 ? exportFilters.jobTitles : null,
            pay_types: exportFilters.payTypes.length > 0 ? exportFilters.payTypes : null,
            employment_status: exportFilters.employmentStatus
          }
        };

        response = await axios.post(`${API}/export/data`, payload, {
          responseType: 'blob',
          timeout: 120000
        });

        // Detect async-required JSON response (large Excel files)
        const contentType = response.headers?.['content-type'] || '';
        if (contentType.includes('application/json') && format === 'excel') {
          const text = await response.data.text();
          const jsonResp = JSON.parse(text);
          if (jsonResp.async_required) {
            await startAsyncExcelPolling(payload, toastId);
            return;
          }
        }

        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match) filename = match[1].replace(/['"]/g, '');
        }
        if (!filename) {
          const extensions = { csv: '.csv', excel: '.xlsx', json: '.json' };
          filename = `overtimeiq_${exportType}${filterSuffix}_${dateStr}${extensions[format] || '.csv'}`;
        }
      }

      triggerBlobDownload(new Blob([response.data]), filename);
      setExportProgress(prev => ({ ...prev, status: 'complete' }));
      toast.success('Export completed!', { id: toastId });
      setTimeout(() => setExportProgress(null), 2000);

    } catch (error) {
      console.error('Export error:', error);
      setExportProgress(prev => ({ ...prev, status: 'error' }));

      if (error.response?.status === 404) {
        toast.error('Export endpoint not available. Please try again later.', { id: toastId });
      } else if (error.response?.status === 500) {
        toast.error('Export timed out or server error. Try narrowing your date range or filters.', { id: toastId });
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Export timed out. Try a shorter date range, fewer filters, or CSV format instead of Excel.', { id: toastId });
      } else {
        toast.error('Failed to export data. Please try again.', { id: toastId });
      }

      setTimeout(() => setExportProgress(null), 3000);
    } finally {
      setExporting(false);
    }
  }, [API, exportFilters, exportGranularity, buildFilterContext, buildComprehensiveFilters]);

  return { exporting, exportProgress, handleExport };
}
